import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchReport, resolveReport, banUser, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A6.15/16 — Shikoyat kartasi (kontent + kontekst) + A6.19 qaror. GET
 * /admin/reports/:id, POST /admin/reports/:id/resolve (backend `4f05814`).
 * «Hal qildim» (resolved) va «Rad etaman» (dismissed) — moderator qarori,
 * serverда audit. Bayt-preview media-proxy'ni kutadi (follow-up).
 */
export function ReportCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [report, setReport] = useState<AdminReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [resolution, setResolution] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchReport(Number(id), ac.signal)
      .then((r) => {
        if (alive) setReport(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const decide = async (dismiss: boolean) => {
    setBusy(true)
    try {
      await resolveReport(Number(id), resolution, dismiss)
      toast(dismiss ? t.mod.dismissedToast : t.mod.resolvedToast, { tone: 'success' })
      navigate('/moderation')
    } catch (e) {
      toast(t.mod.decideFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
      setBusy(false)
    }
  }

  // (1) Moderatsiya→ijro: shikoyat qilingan userni bloklaydi VA shikoyatni yopadi.
  // Ban sababi = izoh yoki «shikoyat #id: kategoriya» (server ≥3 belgi talab qiladi).
  const banAndResolve = async (r: AdminReport) => {
    setBusy(true)
    const reason = resolution.trim() || `report #${r.id}: ${r.category}`
    try {
      await banUser(r.target_id, reason)
      await resolveReport(r.id, reason, false)
      toast(t.mod.bannedToast, { tone: 'success' })
      navigate('/moderation')
    } catch (e) {
      toast(t.mod.decideFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
      setBusy(false)
    }
  }

  const targetLink = (r: AdminReport): ReactNode => {
    if (r.target_type === 'user') return <Link to={`/users/${r.target_id}`} className="text-accent hover:underline">user#{r.target_id}</Link>
    if (r.target_type === 'group' || r.target_type === 'channel')
      return <Link to={`/groups/${r.target_id}`} className="text-accent hover:underline">{r.target_type}#{r.target_id}</Link>
    return <MonoValue value={`${r.target_type}#${r.target_id}`} />
  }

  return (
    <>
      <PageHeader
        title={report ? `#${report.id} · ${report.category}` : t.mod.reportCard}
        screenId="A6.15"
        actions={
          <Link
            to="/moderation"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.mod.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !report ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3">
                <StatusBadge
                  status={report.status === 'pending' ? 'pending' : report.status === 'resolved' ? 'active' : 'deleted'}
                />
              </div>
              <Field label={t.mod.category}>{report.category}</Field>
              <Field label={t.mod.target}>{targetLink(report)}</Field>
              <Field label={t.mod.reporter}>
                <Link to={`/users/${report.reporter_id}`} className="text-accent hover:underline">
                  {report.reporter_username ? `@${report.reporter_username}` : `#${report.reporter_id}`}
                </Link>
              </Field>
              <Field label={t.mod.reason}>{report.reason || '—'}</Field>
              <Field label={t.col.time}>
                <RelativeTime value={report.created_at} />
              </Field>
              {report.status !== 'pending' && (
                <Field label={t.mod.resolution}>{report.resolution || '—'}</Field>
              )}
            </div>

            {report.status === 'pending' && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="mb-2 text-sm font-medium text-secondary">{t.mod.decision}</div>
                <textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder={t.mod.resolutionPlaceholder}
                  rows={2}
                  className="mb-3 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
                  >
                    {t.mod.dismiss}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => decide(false)}
                    className="rounded-md border border-border-strong bg-surface-1 px-3 py-1.5 text-sm text-primary hover:border-accent disabled:opacity-50"
                  >
                    {t.mod.resolve}
                  </button>
                  {report.target_type === 'user' && can(PERMISSIONS.USERS_BAN) && (
                    <button
                      disabled={busy}
                      onClick={() => banAndResolve(report)}
                      className="rounded-md border border-danger bg-danger-subtle px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
                    >
                      {t.mod.banUser}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
